import { gql } from '@apollo/client/core'

/**
 * Fetch task information for mentioned task IIDs
 * @param {ApolloClient} apolloClient - Apollo client instance
 * @param {string} projectId - Project ID
 * @param {Array<number>} taskIds - Array of task IIDs to fetch
 * @returns {Promise<Object>} - Map of taskIid -> { iid, id, title, projectId }
 */
export async function fetchMentionedTasks(apolloClient, projectId, taskIds) {
  if (!apolloClient || !projectId || !taskIds || taskIds.length === 0) {
    return {}
  }

  const taskMap = {}

  // Fetch each task individually using the project-scoped task(iid) lookup
  // We could batch this, but for simplicity we'll fetch one by one
  // In the future, we could optimize this with a batch query
  const fetchPromises = taskIds.map(async taskId => {
    try {
      const TASK_QUERY = gql`
        query TaskById($projectId: ID!, $taskId: ID!) {
          project(id: $projectId) {
            id
            task(id: $taskId) {
              id
              iid
              title
              archived
              project {
                id
              }
            }
          }
        }
      `

      const { data } = await apolloClient.query({
        query: TASK_QUERY,
        variables: {
          projectId: projectId,
          taskId: taskId.toString(),
        },
        fetchPolicy: 'cache-first', // Use cache if available
      })

      if (data?.project?.task && !data.project.task.archived) {
        const task = data.project.task
        const iid = Number(task.iid)
        if (iid > 0) {
          taskMap[iid] = {
            iid: iid,
            id: Number(task.id),
            title: task.title,
            projectId: task.project.id,
          }
        }
      }
    } catch (error) {
      // Silently fail for individual tasks - they just won't show titles
      console.warn(`Failed to fetch task ${taskId}:`, error)
    }
  })

  await Promise.all(fetchPromises)
  return taskMap
}
